import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import FormInput from '../../common/FormInput';
import directDebitLogo from '../../../images/directdebit.gif';
import Checkbox from '../../common/checkbox/checkbox';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useBankDetailsForm } from './useBankDetailsForm';
import { AccountType } from 'Constants/Common';
import { useLocation } from 'react-router-dom';
import PageTitle from '../../common/PageTitle';
import BaseAlert, { AlertId, AlertTypes } from '../../common/alerts/BaseAlert';
import BankDetailsForm from './BankDetailsForm';
import ErrorSummary from '../../common/ErrorSummary/ErrorSummary';
import Button from '../../common/button/Button';
import { getLocalPhone } from 'helpers/location';
import { useAppSelector } from 'state/hooks';
import { selectSiteCountry } from 'state/selectors/site';

const AmendBankDetailsPage = (): JSX.Element => {
    const { t } = useTranslation();
    const location = useLocation();
    const siteLocation = useAppSelector(selectSiteCountry);

    const [expanded, setExpanded] = useState(true);
    const navigationState = (location.state as {
        businessPartnerId: number;
        referenceNumber?: string;
    }) ?? { businessPartnerId: 0, referenceNumber: '' };
    const businessPartnerId = navigationState.businessPartnerId;
    const referenceNumber = navigationState.referenceNumber ?? '';
    const {
        scrollDivRef,
        bankDetails,
        bankDetailErrors,
        daysOptions,
        onChange,
        onSortCodeChange,
        onPaymentDayChange,
        getErrors,
        onSubmit,
        agreedDirectDebit,
        setAgreedDirectDebit,
        agreedDirectDebitDueDate,
        setAgreedDirectDebitDueDate,
        onBack
    } = useBankDetailsForm({
        onSuccess: () => {
            toast.success(t('direct_debit_guarantee.update-bank-success'), {
                onClose: () => {
                    onBack();
                }
            });
        },
        t,
        requireAgreements: true,
        showPaymentDay: false,
        accountType: AccountType.Collections,
        referenceNumber,
        businessPartnerId: businessPartnerId
    });

    const phone =
        getLocalPhone(siteLocation, { scenario: 'payment' }) || t('phone');

    return (
        <div ref={scrollDivRef}>
            <div className='card page-content'>
                <PageTitle title={t('amend_bank_details_title')} />

                <BaseAlert
                    alertType={AlertTypes[AlertId.Warning]}
                    includeImage
                    message={t('amend_bank_details_warning', { phone })}
                    className='m-3 mb-0'
                />

                <p className='p-3 mb-0'>
                    {t('amend_bank_details_instruction')}
                </p>

                <BankDetailsForm
                    bankDetails={bankDetails}
                    bankDetailErrors={bankDetailErrors}
                    daysOptions={daysOptions}
                    t={t}
                    onChange={onChange}
                    onSortCodeChange={onSortCodeChange}
                    onPaymentDayChange={onPaymentDayChange}
                    showPaymentDay={false}
                />
                <Accordion
                    className='mb-0 mt-0 header-background'
                    expanded={expanded}
                    onChange={() => setExpanded(!expanded)}>
                    <AccordionSummary
                        className='accordion-header'
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls='vehiclePaneld-content'
                        id='vehiclePaneld-header'>
                        <p className='p1'>
                            {t('direct_debit_guarantee_title')}
                        </p>
                    </AccordionSummary>
                    <AccordionDetails>
                        <div className='d-flex'>
                            <p className='flex-grow-1 flex-shrink-1 fw-bold'>
                                {t('the_direct_debit_guarantee_title')}
                            </p>
                            <div>
                                <img
                                    src={directDebitLogo}
                                    alt='Direct Debit Logo'
                                />
                            </div>
                        </div>
                        <ul>
                            <li>{t('direct_debit_guarantee.text_1')}</li>
                            <li>{t('direct_debit_guarantee.text_2')}</li>
                            <li>
                                {t('direct_debit_guarantee.text_3')}
                                <br />* {t('direct_debit_guarantee.text_4')}
                            </li>
                            <li>{t('direct_debit_guarantee.text_5')}</li>
                        </ul>
                    </AccordionDetails>
                </Accordion>
                <FormInput
                    id='direct-debit-agreement'
                    errorText={bankDetailErrors.directDebitPolicy}>
                    <div className='d-flex flex-column gap-3'>
                        <Checkbox
                            id='direct-debit-agreement1'
                            checked={agreedDirectDebit}
                            text={t('direct-debit-agreement1')}
                            onChange={() =>
                                setAgreedDirectDebit(!agreedDirectDebit)
                            }
                        />
                        <Checkbox
                            id='direct-debit-agreement2'
                            checked={agreedDirectDebitDueDate}
                            text={t('direct-debit-agreement2')}
                            onChange={() =>
                                setAgreedDirectDebitDueDate(
                                    !agreedDirectDebitDueDate
                                )
                            }
                        />
                    </div>
                </FormInput>
                <div className='card-footer page-content-footer'>
                    <ErrorSummary
                        title={t('page_errors')}
                        errors={getErrors()}
                        className='card-error'
                    />
                </div>
                <div className='bottom-navigation'>
                    <Button
                        text={t('submit')}
                        className='btn btn-primary progress-button'
                        onClick={onSubmit}
                    />
                    <Button
                        text={t('back')}
                        className='btn btn-secondary progress-button'
                        onClick={onBack}
                    />
                </div>
            </div>
        </div>
    );
};

export default AmendBankDetailsPage;
